import APIService from '@/api/DeprecatedAPIService'
import { EventDetails } from '@/api/Helpers'
import { Interval } from '@/helpers/ConflictHelpers'
import { buildQueryString } from '@/helpers/QueryStringParameters'
import { handleUnlimitedCapacity, normalizeSessions } from '@/helpers/SessionHelpers'
import { quantitiesPayload } from '@/helpers/TicketTypeQuantities'
import moment, { Moment } from 'moment-timezone'

export interface Session extends EventSession {
  startMoment: Moment
  endMoment: Moment
  availableCapacity: number
  priceIncrease?: number
}

export function fetchFirstAvailableSession(event: EventDetails, tgIds?: string[]) {
  const params = {
    sold_out: 'F',
    _sort: 'start_datetime',
    _limit: 1,
  }

  if (tgIds) {
    params['ticket_group_id._in'] = tgIds.join(',')
  }

  return fetchSession(event, params)
}

function fetchSession(event: EventDetails, params?: Dict<Primitive>): Promise<Session | null> {
  return (
    fetchSessions(event, params)
      // TODO Remove this guard.
      .then((sessions) => {
        if (sessions.length > 1) {
          throw new Error('Only one session was expected, but API returned multiple.')
        }
        return sessions
      })
      .then((sessions) => (sessions ? sessions[0] : null))
  )
}

function fetchSessions(event: EventDetails, params?: Dict<Primitive>): Promise<Session[]> {
  const id = event.id
  const timezone = event.venue.timezone

  return APIService.getCached(`events/${id}/sessions`, params).then((response) => {
    return response.event_session!._data.map((session) => {
      const { capacity, used_capacity, end_datetime, start_datetime } = session
      return {
        ...session,
        startMoment: moment(start_datetime).tz(timezone),
        endMoment: moment(end_datetime).tz(timezone),
        availableCapacity: handleUnlimitedCapacity(capacity) - used_capacity,
      }
    })
  })
}

export function fetchSessionsOnDate(event: EventDetails, date: Moment, tgIds: string[]): Promise<Session[]> {
  return fetchNormalizedSessions(event, {
    _ondate: date.format('YYYY-MM-DD'),
    'ticket_group.id._in': tgIds.join(','),
  })
}

export function fetchSessionsBetween(
  event: EventDetails,
  { start, end }: Interval,
  tgIds: string[],
): Promise<Session[]> {
  return fetchNormalizedSessions(event, {
    'start_datetime._gte': start.toISOString(),
    'start_datetime._lt': end.toISOString(),
    'ticket_group.id._in': tgIds.join(','),
  })
}

function fetchNormalizedSessions(event: EventDetails, params: Dictionary): Promise<Session[]> {
  return APIService.getCached<'event_session'>(`events/${event.id}/sessions`, params).then((response) =>
    normalizeSessions(response, event.venue),
  )
}

export function fetchSessionsForDateForMinimumQuantities(
  eventId: String,
  venue: Venue,
  date: Moment,
  quantities: TicketTypeQuantities,
): Promise<{
  sessions: Session[]
  priceSchedules: PriceSchedule[]
}> {
  const params = {
    _ondate: date.format('YYYY-MM-DD'),
    _sort: 'start_datetime',
    _include_sold_out: true,
  }

  const url = `events/${eventId}/sessions?` + buildQueryString(params)
  return APIService.post<'event_session' | 'price_schedule'>(url, quantitiesPayload(quantities)).then((response) => {
    return {
      sessions: normalizeSessions(response, venue),
      priceSchedules: response.price_schedule._data,
    }
  })
}
